import { CommonModule, DOCUMENT, isPlatformServer, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Inject,
  input,
  PLATFORM_ID,
  signal,
  TemplateRef,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import {
  AnimateModule,
  GtmClickDirective,
  IUser,
  TrainingBrand,
  WwwAppStateLogout,
  WwwRouteSlug,
} from '@pro-connect/shared';
import { fromEvent } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { IAccountOrder } from 'shared/src/pages/core/pages/account/models/account-order';
import { AccountStateReset } from 'shared/src/pages/core/pages/account/account.state';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { HeaderCartComponent } from '../header-cart/header-cart.component';
import { Angulartics2GoogleTagManager } from 'angulartics2';

@Component({
  host: { ngSkipHydration: 'true' },
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    NgOptimizedImage,
    AnimateModule,
    NgTemplateOutlet,
    NgbDropdownModule,
    HeaderCartComponent,
    GtmClickDirective,
  ],
})
export class HeaderComponent implements AfterViewInit {
  public readonly user = input<IUser | null>(null);
  public readonly border = input<boolean>(false);
  public readonly accountOrders = input<IAccountOrder[]>([]);

  // public readonly logout = output<void>();
  public readonly isHeaderFixed = signal(false);
  public readonly cartActive: WritableSignal<boolean> = signal(false);
  public readonly navbarExpanded: WritableSignal<boolean> = signal(false);
  public readonly navbarFocusedDesktop: WritableSignal<boolean> = signal(false);
  public readonly navbarExtContentActive: WritableSignal<TemplateRef<{ direction: string }> | null> = signal(null);
  private readonly _angulartics2GoogleTagManager = inject(Angulartics2GoogleTagManager);

  public readonly WwwRouteSlug = WwwRouteSlug;
  protected readonly Brand = TrainingBrand;
  private readonly _store: Store = inject(Store);
  private readonly _router: Router = inject(Router);

  constructor(
    @Inject(DOCUMENT)
    private readonly _document: Document,
    @Inject(PLATFORM_ID)
    private readonly _platformId: object,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _destroyRef: DestroyRef,
  ) {
    this._closeAnyExtendedWindowsOnRouterChange();
  }

  public toggleNavbar(): void {
    this.navbarExpanded.set(!this.navbarExpanded());
  }

  public ngAfterViewInit(): void {
    if (isPlatformServer(this._platformId)) {
      return;
    }
    this._setScrollSubscription();
  }

  public onWindowScroll(): void {
    const scrollOffset = this._document.body.scrollTop || window.pageYOffset;
    this.isHeaderFixed.set(scrollOffset > 80);
    this._changeDetectorRef.markForCheck();
  }

  public logout(): void {
    this._store.dispatch([new AccountStateReset(), new WwwAppStateLogout()]);
  }

  public logoClick(): void {
    this._angulartics2GoogleTagManager.pushLayer({
      event: 'uaevent',
      ecommerce: 'undefined',
      eventCategory: 'main menu navigation',
      eventAction: `select`,
      eventLabel: 'logo',
      event_name: 'logo_click',
    });
  }

  public pushLayer(eventLabel: string, clickArea: string, eventAction: string = 'select::header'): void {
    this._angulartics2GoogleTagManager.pushLayer({
      event: 'uaevent',
      ecommerce: 'undefined',
      eventCategory: 'main menu navigation',
      eventAction: eventAction,
      eventLabel: eventLabel,
      event_name: 'menu_click',
      click_area: clickArea,
      breadcrumb: eventLabel,
    });
  }

  private _setScrollSubscription(): void {
    fromEvent(window, 'scroll')
      .pipe(takeUntilDestroyed(this._destroyRef), debounceTime(25))
      .subscribe(() => {
        this.onWindowScroll();
      });
  }

  private _closeAnyExtendedWindowsOnRouterChange(): void {
    this._router.events
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((routeChange) => {
          if (routeChange instanceof NavigationEnd) {
            this.cartActive.set(false);
            this.navbarExpanded.set(false);
            this.navbarFocusedDesktop.set(false);
            this.navbarExtContentActive.set(null);
          }
        }),
      )
      .subscribe();
  }
}
